<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { cloneDeep } from "es-toolkit";
import { computed, provide, reactive, ref, watch } from "vue";

import { IPilotingWizardState, PilotingWizardStateInjectKey } from "@/components/piloting/wizard/piloting-wizard.types";
import { IProgrammingGanttData } from "@/components/programming-gantt/programming-gantt";
import {
  generateGanttData,
  IProgrammingDeviceRow,
  IProgrammingDeviceSchedule,
} from "@/components/programming-gantt/programming-gantt.type";
import { useFetchSiteInstalledApplianceCategories } from "@/composables/kili/sites/fetches/use-fetch-site-installed-appliance-categories";
import { useFetchSitePlanningOverrideByApplianceCategory } from "@/composables/kili/sites/fetches/use-fetch-site-planning-override-by-appliance-category";
import { useInputChangeFlag } from "@/composables/use-input-changed-flag";
import { DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES } from "@/data/api/kili/programs/program.type";
import { SitesQueryKeys } from "@/data/api/kili/sites/sites-query-keys";
import { TInstalledApplianceCategories } from "@/data/api/kili/sites/types/installed-appliance-categories.type";
import { TPlanningOverrideBySection, TSectionPlanning } from "@/data/api/kili/sites/types/planning.type";
import { TSite } from "@/data/api/kili/sites/types/sites.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { naturalSort } from "@/utils/array";
import { getProgramApplianceCategories } from "@/utils/programs/program-utils";

import Checkbox from "primevue/checkbox";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import ApplyPilotingWizardStep from "@/components/piloting/wizard/apply-piloting-wizard-step.vue";
import PilotingTimezoneMessage from "@/components/piloting/wizard/piloting-timezone-message.vue";
import SelectSectionsPilotingWizardStep from "@/components/piloting/wizard/select-sections-piloting-wizard-step.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import SelectButton, { TSelectButtonItem } from "@/components/ui/buttons/select-button.vue";
import WizardTab from "@/components/ui/wizard/wizard-tab.vue";
import Wizard from "@/components/ui/wizard/wizard.vue";

interface IProps {
  site?: TSite;
}

const selectStep = ref();
const { t, k } = useTypedIntl();
const queryClient = useQueryClient();

const props = defineProps<IProps>();

const currentIndex = ref(0);
const searchSection = ref("");

const visible = defineModel<boolean>("visible", {
  required: false,
  default: false,
});

const { hasInputBeenUsed: hasSearchSectionInputBeenUsed } = useInputChangeFlag(searchSection);

const { fetch: fetchInstalledApplianceCategories } = useFetchSiteInstalledApplianceCategories();

const { fetch: fetchPlanning, isFetching: isFetchingPlanning } = useFetchSitePlanningOverrideByApplianceCategory();

const selectedApplianceCategory = ref<EProgramApplianceCategory>(DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES[0]);
const plannings = ref<TSectionPlanning[]>([]);

const installedApplianceCategories = ref<TInstalledApplianceCategories>();

const displayedApplianceCategories = computed<TSelectButtonItem<EProgramApplianceCategory>[]>(() =>
  getProgramApplianceCategories(DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES, installedApplianceCategories.value).map(
    mapApplianceCategoryToSelectButtonItem,
  ),
);

const data = computed<IProgrammingGanttData<IProgrammingDeviceRow, IProgrammingDeviceSchedule>>(() => {
  if (!plannings.value) {
    return {
      rows: [] as IProgrammingDeviceRow[],
      schedules: [] as IProgrammingDeviceSchedule[],
    };
  }

  return generateGanttData(plannings.value);
});

const isSelectAllChecked = computed<boolean>({
  get() {
    return state.selection.length === state.data.rows.length;
  },
  set(next: boolean) {
    if (!next) {
      unselectAll();
    } else {
      selectAll();
    }
  },
});

const DEFAULT_STATE: IPilotingWizardState = Object.freeze({
  selection: [],
  data: data.value,
  plannings: plannings.value ?? [],
  siteId: props.site?.siteId ?? -1,
  isPlanningsLoading: isFetchingPlanning.value,
  formMode: "create",
  maybeSelectedSchedule: undefined,
});

const state = reactive<IPilotingWizardState>(cloneDeep(DEFAULT_STATE));

provide<IPilotingWizardState>(PilotingWizardStateInjectKey, state);

const isSubmitButtonDisabled = computed<boolean>(() => {
  if (state.formMode === "create") {
    return state.selection.length === 0;
  } else {
    return false;
  }
});

function mapApplianceCategoryToSelectButtonItem(
  category: EProgramApplianceCategory,
): TSelectButtonItem<EProgramApplianceCategory> {
  return { value: category, label: t(k.global.applianceCategory[category]) };
}

function selectAll() {
  return selectStep.value?.selectAll();
}

function unselectAll() {
  return selectStep.value?.unselectAll();
}

async function refetchPlannings() {
  if (!props.site) return;

  const fetchedPlanning = await fetchPlanning({
    siteId: props.site.siteId,
    applianceCategory: selectedApplianceCategory.value,
  });

  if (fetchedPlanning) {
    plannings.value = naturalSortPlanningBySectionName(fetchedPlanning.sections);
  }
}

function naturalSortPlanningBySectionName(planning: TPlanningOverrideBySection["sections"]) {
  const sectionsCopy = cloneDeep(planning);
  return sectionsCopy.sort((a, b) => {
    return naturalSort(a.sectionName, b.sectionName);
  });
}

async function handleShowWizard() {
  if (!props.site) return;

  installedApplianceCategories.value = await fetchInstalledApplianceCategories(props.site.siteId);
  refetchPlannings();
}

function handleHideWizard() {
  selectedApplianceCategory.value = DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES[0];
}

function resetFormMode() {
  state.formMode = DEFAULT_STATE.formMode;
  state.maybeSelectedSchedule = DEFAULT_STATE.maybeSelectedSchedule;
}

watch(isFetchingPlanning, () => {
  state.isPlanningsLoading = isFetchingPlanning.value;
});

watch(
  () => props.site,
  () => {
    state.siteId = props.site?.siteId ?? -1;
  },
);

watch(data, () => {
  state.data = data.value;
});

watch(plannings, () => {
  state.plannings = plannings.value ?? [];
});

watch(
  visible,
  (newValue) => {
    currentIndex.value = 0;
    state.selection = [];
    state.data = data.value;
    searchSection.value = "";

    if (newValue) {
      MixpanelService.trackEvent(EMixpanelEvent.MANUAL_CONTROL_SECTION_LIST);
    }
  },
  { immediate: true },
);

watch(hasSearchSectionInputBeenUsed, () => {
  MixpanelService.trackEvent(EMixpanelEvent.MANUAL_CONTROL_SECTION_SEARCH_PERFORMED);
});

watch(selectedApplianceCategory, async (newCategory) => {
  if (!newCategory || !props.site) return;
  refetchPlannings();
});

watch(
  () => props.site,
  (next, prev) => {
    if (!next && prev) {
      // clear planning overrides cache for the previously selected site
      queryClient.removeQueries({
        queryKey: SitesQueryKeys.planningOverrides(prev.siteId),
      });
    }
  },
);
</script>

<template>
  <Wizard
    v-model:current-tab-index="currentIndex"
    v-model:visible="visible"
    class="w-[80vw]"
    :class="{
      '!w-[60vw]': currentIndex !== 0,
    }"
    @finish="visible = false"
    @hide="handleHideWizard"
    @show="handleShowWizard"
  >
    <template #header>
      <div class="w-full flex flex-row items-center justify-between gap-4">
        <div class="flex flex-row items-center justify-start gap-2 w-full">
          <div class="w-[48px] h-[48px] flex items-center justify-center border border-gray-200 rounded-lg">
            <i class="pi pi-building" />
          </div>
          <div class="flex flex-col gap-0">
            <h3 class="font-semibold text-[18px] p-0 m-0">
              {{ props.site?.name }}
            </h3>
            <span class="text-[14px] font-normal text-text-secondary">
              {{ t(k.wizard.piloting.subtitle[state.formMode]) }}
            </span>
          </div>
        </div>
        <IconField
          v-if="currentIndex === 0"
          class="w-full"
        >
          <InputIcon class="pi pi-search" />
          <InputText
            v-model.trim="searchSection"
            data-test-id="piloting-wizard-search-section-input"
            :placeholder="t(k.wizard.piloting.filterSectionsPlaceholder)"
          />
        </IconField>
      </div>
    </template>

    <template #default="slotProps">
      <WizardTab
        :current-idx="slotProps.idx"
        :tabs-length="slotProps.tabsLength"
        lazy
        @activated="resetFormMode"
      >
        <PilotingTimezoneMessage
          class="mb-4 mt-1"
          :site="props.site"
        />

        <div class="pb-4 flex flex-col gap-2">
          <SelectButton
            v-model="selectedApplianceCategory"
            :items="displayedApplianceCategories"
          />
        </div>
        <SelectSectionsPilotingWizardStep
          v-if="visible"
          ref="selectStep"
          :search="searchSection"
          :selected-appliance-category="selectedApplianceCategory"
          @refresh-plannings="refetchPlannings"
        />

        <template #footer="{ focusNextTab }">
          <div class="w-full flex flex-row items-center justify-between">
            <div class="flex flex-row items-center justify-start gap-2">
              <Checkbox
                id="select-all-gantt"
                v-model="isSelectAllChecked"
                aria-labelledby="select-all-gantt-label"
                binary
                data-test-id="piloting-wizard-select-all-sections-checkbox"
                :indeterminate="state.selection.length > 0 && state.selection.length < state.data.rows.length - 1"
                @keydown.enter="isSelectAllChecked = !isSelectAllChecked"
              />
              <label
                id="select-all-gantt-label"
                for="select-all-gantt"
              >
                {{ t(k.global.misc.selectAll) }}
              </label>
            </div>

            <div class="flex flex-row items-center justify-between gap-2">
              <HospitalityButton
                data-test-id="piloting-wizard-close-button"
                severity="secondary"
                @click="visible = false"
              >
                {{ t(k.global.misc.close) }}
              </HospitalityButton>
              <HospitalityButton
                data-test-id="piloting-wizard-next-button"
                :disabled="state.selection.length === 0"
                @click="focusNextTab"
              >
                {{ t(k.wizard.piloting.planMode) }}
                <span class="bg-white rounded-full w-[24px] h-[24px] flex items-center justify-center text-brand ml-2">
                  {{ state.selection.length }}
                </span>
              </HospitalityButton>
            </div>
          </div>
        </template>
      </WizardTab>
      <WizardTab
        lazy
        :current-idx="slotProps.idx"
        :tabs-length="slotProps.tabsLength"
      >
        <ApplyPilotingWizardStep
          :form-mode="state.formMode"
          :maybe-selected-row-schedule="state.maybeSelectedSchedule"
          :selected-appliance-category="selectedApplianceCategory"
          :site="props.site"
          @refresh-plannings="refetchPlannings"
        />

        <template #footer="{ focusPrevTab, focusNextTab }">
          <div class="w-full flex flex-row items-center justify-end">
            <div class="flex flex-row items-center justify-between gap-2">
              <HospitalityButton
                data-test-id="piloting-wizard-previous-button"
                severity="secondary"
                @click="focusPrevTab"
              >
                {{ t(k.global.misc.previous) }}
              </HospitalityButton>
              <HospitalityButton
                data-test-id="piloting-wizard-submit-button"
                :disabled="isSubmitButtonDisabled"
                @click="focusNextTab"
              >
                {{ t(k.global.misc.confirm) }}
              </HospitalityButton>
            </div>
          </div>
        </template>
      </WizardTab>
    </template>
  </Wizard>
</template>
